import { useEffect, useMemo } from 'react';
import { IntakeFormQueries, prepareIntakeFormStoreInfoFromLocationList } from '@frontend/api-intake-form';
import { getUser, isWeaveUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { INTAKE_FORM_V2_FEATURE_FLAG } from '../constants';

const DEFAULT_INTAKE_FORM_CONFIG = {
  isShowIntakeForm: false,
  ownIntakeFormLocations: [],
  intakeFormLocations: [],
  ownIncompleteIntakeForms: [],
};

export const useGetLocationHierarchyConfig = ({
  showErrorAlert,
  prepareIntakeFormConfig,
  enabled = true,
}: {
  showErrorAlert?: boolean;
  prepareIntakeFormConfig?: boolean;
  enabled?: boolean;
}) => {
  const { selectedLocationIds, selectedGroupId } = useAppScopeStore();
  const alert = useAlert();
  const { t } = useTranslation('onboarding');

  const { getFeatureFlagLocationMap } = useScopedAppFlagStore();

  const selectedParentId = selectedGroupId;
  const locationIdForQuery = selectedParentId ?? selectedLocationIds?.[0] ?? '';
  const isParentLocation = !!selectedParentId;

  const {
    data: locationHierarchyConfig,
    isLoading,
    isError,
    isFetched: isFetchedLocationHierarchyConfig,
  } = IntakeFormQueries.useIntakeFormLocationsQuery(locationIdForQuery, { enabled });

  useEffect(() => {
    if (!showErrorAlert) {
      return;
    }
    const hasErrorInIntakeFormLocations = locationHierarchyConfig?.some(({ error }) => !!error);

    // Show alert only in single location or api failure
    if (isError || (!isParentLocation && hasErrorInIntakeFormLocations)) {
      alert.error(t('Failed to retrieve onboarding form(s) meta data.'));
    }
  }, [alert, locationHierarchyConfig, isError, isParentLocation, showErrorAlert, t]);

  const intakeFormLocationsConfig = useMemo<ReturnType<typeof prepareIntakeFormStoreInfoFromLocationList>>(() => {
    if (isLoading || !prepareIntakeFormConfig) {
      return DEFAULT_INTAKE_FORM_CONFIG;
    }

    const user = getUser();
    return prepareIntakeFormStoreInfoFromLocationList({
      intakeFormLocations: locationHierarchyConfig ?? [],
      userEmail: user?.username,
      isParentLocation,
      isWeaveUser: isWeaveUser(),
      selectedLocationIds,
    });
  }, [isLoading, prepareIntakeFormConfig, locationHierarchyConfig, isParentLocation, selectedLocationIds]);

  const fastPassFFLocationMap = useMemo(
    () => getFeatureFlagLocationMap(INTAKE_FORM_V2_FEATURE_FLAG),
    [getFeatureFlagLocationMap]
  );

  const {
    fastPassIntakeFormLocationsConfig,
    fastPassLocationIds,
    pendingFastPassIntakeFormLocationsConfig,
    isLoadingFastPassEligibility,
  } = useMemo(() => {
    const _isLoadingFastPassEligibility = intakeFormLocationsConfig.intakeFormLocations.some((location) => {
      return fastPassFFLocationMap?.[location.locationId]?.value === undefined;
    });

    const _fastPassIntakeFormLocationsConfig = intakeFormLocationsConfig.intakeFormLocations.filter((location) => {
      const hasIntakeFormV2FF = !!fastPassFFLocationMap?.[location.locationId]?.value;

      if (!hasIntakeFormV2FF || !location.isFastPassEligibleLocation) {
        return false;
      }
      return true;
    });

    const _pendingFastPassIntakeFormLocationsConfig = _fastPassIntakeFormLocationsConfig?.filter(
      ({ formCompletedAt }) => !formCompletedAt
    );

    return {
      fastPassIntakeFormLocationsConfig: _fastPassIntakeFormLocationsConfig,
      isLoadingFastPassEligibility: _isLoadingFastPassEligibility,
      pendingFastPassIntakeFormLocationsConfig: _pendingFastPassIntakeFormLocationsConfig,
      fastPassLocationIds: _fastPassIntakeFormLocationsConfig?.map((loc) => loc.locationId),
    };
  }, [fastPassFFLocationMap, intakeFormLocationsConfig?.intakeFormLocations]);

  return {
    fastPassIntakeFormLocationsConfig,
    fastPassLocationIds,
    isLoading,
    intakeFormLocationsConfig,
    isFetchedLocationHierarchyConfig,
    isLoadingFastPassEligibility,
    locationHierarchyConfig,
    locationIdForQuery,
    pendingFastPassIntakeFormLocationsConfig,
  };
};
